import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { toast } from 'react-toastify';
import { tableHelper } from 'common/helpers/table/table-helper';
import {
    MasterLayout,
    MainLayout,
    ToolbarLayout,
    ToolbarLayoutLeft,
    ToolbarLayoutRight,
} from 'common/imports/content-layout';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import ActionButton from 'common/partials/ActionButton';
import { AgentVersionModel as Model } from '../model/agent-versions.model'
import { AgentVersionService as Service } from '../services/agent-versions.service';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import download from 'common/helpers/download/download';
import { NotesModal } from './NotesModal';

interface Notes {
    IsOpen: boolean;
    Version?: Model;
}

export function AgentVersions() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState<Model[]>([]);
    const [notes, setNotes] = useState<Notes>({ IsOpen: false });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(100);
    const [sorting, setSorting] = useState<{ orderBy?: string; order?: 'asc' | 'desc' }>({
        orderBy: 'Id',
        order: 'desc'
    });

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await Service.getDataList({
                pageNumber: currentPage,
                pageSize: rowsPerPage,
                orderBy: sorting.orderBy,
                order: sorting.order
            });
            if (response && !response.hasErrors) {
                setTableData(response.data.items);
                setTotalRows(response.data.totalCount);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadButton = async (entity: Model) => {
        if (entity.hash) {
            const blobResponse = await Service.downloadFile(entity.version);
            if (blobResponse) {
                download(blobResponse, `agent-${entity.version}.zip`);
            }
        }
        else {
            toast.error(`${t('agentVersions.download.errorMessage')}`);
        }
    }

    const handleCopyButton = (entity: Model) => {
        navigator.clipboard.writeText(entity.hash);
        toast.info(`${t('agentVersions.copied')}`)
    }

    const handleOpenDetails = (entity: Model) => {
        setNotes({ IsOpen: true, Version: entity });
    }

    const handleCloseDetails = () => {
        setNotes({ IsOpen: false });
    }

    const columns: TableColumn<Model>[] = [
        {
            name: `${t('agentVersions.tableHeaders.id')}`,
            selector: (row) => row.id || '',
            sortable: true,
            sortField: 'Id',
            width: '75px'
        },
        {
            name: `${t('agentVersions.tableHeaders.name')}`,
            selector: (row) => row.version || '',
            sortable: true,
            sortField: 'Name'
        },
        {
            name: `${t('agentVersions.tableHeaders.release')}`,
            selector: (row) => row.releaseIn || '',
            sortable: true,
            sortField: 'CreationDate'
        },
        {
            name: `${t('agentVersions.tableHeaders.actions')}`,
            cell: (row) => (
                <div className="d-grid d-flex gap-1">
                    <ActionButton type='details' onClick={() => handleOpenDetails(row)} />
                    <ActionButton type='download' onClick={() => handleDownloadButton(row)} disabled={(row.hash == null)} />
                    <ActionButton type='copy' onClick={() => handleCopyButton(row)} disabled={(row.hash == null)} />
                </div>
            ),
            width: '150px'
        }
    ];

    const handleListSort = (selectedColumn: TableColumn<Model>, sortDirection: 'asc' | 'desc') => {
        setSorting({
            orderBy: selectedColumn.sortField || 'Id',
            order: sortDirection
        });

        setTableData([]);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, rowsPerPage, sorting]);

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight />
            </ToolbarLayout>
            <MainLayout>
                <NotesModal IsOpen={notes.IsOpen} Version={notes.Version} Close={handleCloseDetails} />
                <div className="alert alert-warning mb-5 p-5" role="alert">
                    <h4 className="alert-heading">{t('agentVersions.validationFile.title')}</h4>
                    <p>{t('agentVersions.validationFile.description')} </p>
                    <div className="border-bottom border-dark opacity-20 mb-5"></div>
                    <p className="mb-0">{t('agentVersions.validationFile.steps')} <code>(Get-FileHash {t('agentVersions.validationFile.file')}).Hash -eq '{t('agentVersions.validationFile.copied')}'</code></p>
                </div>
                <div className="card card-flush shadow-none mt-10">
                    <div className="card-body p-0">
                        <DataTable
                            title={false}
                            persistTableHead={true}
                            columns={columns}
                            data={tableData}
                            noDataComponent={tableHelper.NoDataComponent()}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationPerPage={rowsPerPage}
                            paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                            onChangePage={(page) => setCurrentPage(page)}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                setRowsPerPage(newPerPage);
                                setCurrentPage(page);
                            }}
                            paginationComponentOptions={tableHelper.PaginationComponentOptions()}
                            highlightOnHover
                            pointerOnHover
                            progressPending={isLoading}
                            progressComponent={tableHelper.Loader()}
                            customStyles={itLabTableStyle}
                            dense
                            onSort={handleListSort}
                            sortServer
                        />
                    </div>
                </div>
            </MainLayout>
        </MasterLayout>
    )

}


